import React, { useCallback, useContext, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import {
    loginThenComeBack as loginThenComeBackThunk,
    trackV2,
} from '@gumtree/shell/src/common/common-actions';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { isOutOfCoreHours } from '@gumtree/shared/src/util/core-hours';
import { stickyScroll } from './sticky-scroll';
import revealPhoneUtil from '../seller-contact-block/reveal-phone-util';
import type { VipState, VipUseDispatch } from '../../reducers';
import ContactButtons, { ContactType } from '../contact-buttons/contact-buttons';
import MakeOffer from '../seller-contact-block/make-offer/make-offer';
import {
    H2Large,
    H2Small,
    StickyButtonsContainer,
    StickyHeaderContainer,
    StickyHeaderFnRow,
    StickyHeaderTxtRow,
    PostingLength,
    StickyHeaderMessageTipRow,
    StickyHeaderMessageTipTitle,
    StickyHeaderMessageTipContent,
    BuyStickyHeaderTxtRow,
} from './style';
import SellerPhone from '../seller-phone/app';
import { RatingsView } from '../seller-avatar-block';
import { MotorsCTAContext, RequestAQuoteFormContext } from '../../vip-context';
import { fetchPostCallReminder } from '../../reducers/post-call';
import { BuyButton } from '../seller-buy-block';
import RenderPriceBlock from '../seller-buy-block/render-price-block';

const StickyHeader: React.FC<{
    isPurchaseAvailable?: boolean;
    setIsBuyerProtectionOpen?: (isOpen: boolean) => void;
}> = ({ isPurchaseAvailable, setIsBuyerProtectionOpen }) => {
    const {
        sellerContactDetails,
        sellerRating,
        adDetails,
        baseConfig,
        sellerActions: { makeAnOfferEnabled },
        postCall,
    } = useSelector(selectState, shallowEqual);

    const { adSource } = useContext(RequestAQuoteFormContext);

    const {
        contactName,
        userLoggedIn,
        replyPhone,
        replyEmail,
        replyLink,
        revealedNumber,
        replySmsEnabled,
        showRevealNumber,
        isLoginRequiredToContactForVipCategory,
        revealNumberUnmasked,
        revealPhoneCallbackUrl,
    } = sellerContactDetails;
    const {
        postingSince,
        pro,
        l1CategorySeoName,
        containsCallTrackingFeature,
        price,
        l2CategoryId,
        deliveryFromPrice,
        buyerProtectionPrice,
    } = adDetails;
    const isForSale = l1CategorySeoName === 'for-sale';
    const isJobs = l1CategorySeoName === 'jobs';
    const isMobile = baseConfig.device.type === 'mobile';
    const isServices = l1CategorySeoName === 'business-services';

    const { isMotorsCTATask1, isMotorsCTATask3 } = useContext(MotorsCTAContext);
    const skipLoginToReveal = isMotorsCTATask1 && !(isMotorsCTATask3 && isOutOfCoreHours());

    const hideSMS = useFeatureIsOn(GrowthBookFeature.HIDE_SMS) && adDetails.isFromMotorsFeed;
    const isUI_202506 =
        useFeatureValue(GrowthBookFeature.SERVICE_NEW_UI, 'A') === 'B' && isServices;
    const showPostCallReminder = postCall.showReminder && isMobile && !isPurchaseAvailable;

    useEffect(() => {
        const cleanup = stickyScroll(isMobile, !!isMotorsCTATask1, isUI_202506);
        return cleanup;
    }, [isMobile, isMotorsCTATask1, isUI_202506]);

    useEffect(() => {
        if (isMotorsCTATask1 && isMobile) {
            dispatch(fetchPostCallReminder());
        }
    }, [isMotorsCTATask1, isMobile]);

    const dispatch = useDispatch() as VipUseDispatch;
    const loginThenComeBack = useCallback(() => dispatch(loginThenComeBackThunk()), []);

    const revealPhone = (e) => {
        trackV2('R2SPhoneBegin');
        revealPhoneUtil(e, dispatch, sellerContactDetails, adDetails, baseConfig);
    };

    const handleRevealPhone = (e) => {
        e.preventDefault();
        revealPhone(e);
    };

    // task1 temporary solution
    const handleClickContactButtonBegin = (type: ContactType) => {
        if (!adDetails.isFromMotorsFeed || !isMobile) return;
        const clickElement = isMotorsCTATask1
            ? type === 'tel'
                ? 'call button'
                : type === 'inlineMessage'
                    ? 'message text link'
                    : 'message button'
            : type === 'tel'
                ? 'sticky header call button'
                : type === 'email'
                    ? 'sticky header message button'
                    : undefined;
        if (!clickElement) return;
        trackGA4Event<GA4.CtaPostCallReminderEvent>({
            event: 'cta_post_call_reminder_click',
            clickElement,
        });
    };

    return (
        <StickyHeaderContainer
            data-q="sticky-banner"
            className={cx({
                'bottom-sticky': (isMotorsCTATask1 && isMobile) || isUI_202506,
                'is-shown': (isUI_202506 || isMotorsCTATask1) && isMobile,
            })}
        >
            {!(isUI_202506 && isMobile) &&
                (isPurchaseAvailable ? (
                    <BuyStickyHeaderTxtRow>
                        <RenderPriceBlock
                            price={price}
                            deliveryFromPrice={deliveryFromPrice}
                            buyerProtectionPrice={buyerProtectionPrice}
                            l2CategoryId={l2CategoryId}
                            isStickyHeader
                            setIsBuyerProtectionOpen={setIsBuyerProtectionOpen}
                        />
                    </BuyStickyHeaderTxtRow>
                ) : (
                    <StickyHeaderTxtRow>
                        <H2Small data-q="seller-name-small-screen">Contact {contactName}</H2Small>
                        <H2Large data-q="seller-name-large-screen">{contactName}</H2Large>
                        {isForSale && sellerRating?.total !== 0 && (
                            <RatingsView
                                average={sellerRating?.average}
                                total={sellerRating?.total}
                            />
                        )}
                        {!isJobs && !showPostCallReminder && (
                            <PostingLength
                                hasRating={isForSale && sellerRating && sellerRating.total !== 0}
                            >
                                Posting for {postingSince}
                            </PostingLength>
                        )}
                    </StickyHeaderTxtRow>
                ))}
            {showPostCallReminder && (
                <StickyHeaderMessageTipRow>
                    <StickyHeaderMessageTipTitle>
                        You called them recently
                    </StickyHeaderMessageTipTitle>
                    <StickyHeaderMessageTipContent>
                        Not able to get through?&nbsp;
                        <ContactButtons
                            onlyInlineMessage
                            isMobile
                            l1CategorySeoName={l1CategorySeoName}
                            replySmsEnabled={false}
                            revealPhoneUtil={() => {}}
                            onClickBegin={handleClickContactButtonBegin}
                            messageEventSource='pcrtext'
                        />
                        &nbsp;and the dealership will get back to you as soon as possible.
                    </StickyHeaderMessageTipContent>
                </StickyHeaderMessageTipRow>
            )}
            <StickyHeaderFnRow pro={pro && containsCallTrackingFeature} isUI_202506={isUI_202506}>
                {!isMobile && replyPhone && !isPurchaseAvailable && (
                    <SellerPhone
                        isUserLoggedIn={userLoggedIn}
                        replyPhone={replyPhone}
                        revealedNumber={revealedNumber}
                        pro={pro}
                        skipLoginToReveal={skipLoginToReveal}
                        containsCallTrackingFeature={containsCallTrackingFeature}
                        revealPhone={handleRevealPhone}
                        revealNumberUnmasked={revealNumberUnmasked}
                        showRevealNumber={showRevealNumber}
                        revealPhoneCallbackUrl={revealPhoneCallbackUrl}
                        isLoginRequiredToContactForVipCategory={
                            isLoginRequiredToContactForVipCategory
                        }
                        l1CategorySeoName={l1CategorySeoName}
                        isStickyHeader
                        loginThenComeBack={loginThenComeBack}
                    />
                )}
                <StickyButtonsContainer
                    isMobile={isMobile}
                    canCall={isMobile && !!replyPhone}
                    canSms={isMobile && replySmsEnabled && !hideSMS}
                    canEmail={!!replyEmail}
                    canOffer={!!replyEmail && makeAnOfferEnabled}
                    canBuy={isPurchaseAvailable}
                    isUI_202506={isUI_202506}
                    adSource={adSource || 'GT'}
                    isMotorsCTATask1={isMotorsCTATask1}
                >
                    <ContactButtons
                        replyLink={replyLink}
                        replyEmail={replyEmail}
                        replyPhone={replyPhone}
                        replySmsEnabled={replySmsEnabled && !hideSMS}
                        skipLoginToReveal={skipLoginToReveal}
                        revealPhoneUtil={revealPhoneUtil}
                        isStickyHeader
                        isMobile={isMobile}
                        l1CategorySeoName={l1CategorySeoName}
                        messageEventSource='pcrbutton'
                        isPurchaseAvailable={isPurchaseAvailable}
                        onClickBegin={handleClickContactButtonBegin}
                    />
                    {!!replyEmail &&
                        makeAnOfferEnabled &&
                        adSource !== 'Bark' &&
                        !isPurchaseAvailable && <MakeOffer dropdown />}

                    {isPurchaseAvailable && (
                        <div style={{ gridColumn: 'buy-button' }}>
                            <BuyButton isStickyHeader />
                        </div>
                    )}
                </StickyButtonsContainer>
            </StickyHeaderFnRow>
        </StickyHeaderContainer>
    );
};

function selectState({
    sellerContactDetails,
    sellerRating,
    adDetails,
    baseConfig,
    sellerActions,
    postCall,
}: VipState) {
    return {
        sellerContactDetails,
        sellerRating,
        adDetails,
        baseConfig,
        sellerActions,
        postCall,
    };
}

export default StickyHeader;
